import { Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment, { Moment } from "moment";
import { useTranslation } from "react-i18next";
import { typography } from "../../utils/typography";
import { semanticColors } from "../../utils/colors";
import { outlinedFieldSx } from "../../utils/fieldStyles";
import { pickerPaperSx } from "../video/PreJoinScreens/formStyles";

export type DateRangeValue = [Moment | null, Moment | null];

/** The pill-shaped buttons under the fields, one per preset range. */
const shortcutChipSx = {
  ...typography.body.s,
  color: semanticColors.text.secondary,
  bgcolor: "transparent",
  border: `1px solid ${semanticColors.border.middle}`,
  borderRadius: "40px",
  px: "10px",
  py: "4px",
  cursor: "pointer",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: semanticColors.border.focus,
    color: semanticColors.text.primary
  }
};

/**
 * Start/end date filter.
 *
 * Built on the MIT `@mui/x-date-pickers`, like every other picker in the app
 * (see ScheduleForm and PatientVideoDialog). It used to use the DateRangePicker
 * from `@mui/x-date-pickers-pro` — MUI X Pro, a commercial package needing a
 * licence key the project does not have, which logged a licence error on every
 * render. Two plain date pickers cover the same need without it.
 *
 * The props are unchanged, so the history filters and the summary screen that
 * mount this through TableFilters keep working as they were.
 */
export default function DateRangePickerComponent({
  titles,
  onChange,
  value
}: {
  titles?: [string, string];
  onChange: (value: DateRangeValue) => void;
  value?: DateRangeValue;
}) {
  const { t } = useTranslation();

  // Defaulted per element, not just on the whole value: one caller initialises
  // the filter as `[]`, and an undefined element would flip a controlled picker
  // to uncontrolled and make React complain.
  const [start = null, end = null] = value ?? [];

  const shortcuts: Array<{ label: string; getValue: () => DateRangeValue }> = [
    {
      label: t("dateRange.last7days"),
      getValue: () => [moment().subtract(7, "day"), moment()]
    },
    {
      label: t("dateRange.thisMonth"),
      getValue: () => [moment().startOf("month"), moment()]
    },
    {
      label: t("dateRange.last3months"),
      getValue: () => [moment().subtract(3, "months"), moment()]
    },
    {
      label: t("dateRange.last6months"),
      getValue: () => [moment().subtract(6, "months"), moment()]
    },
    {
      label: t("dateRange.lastYear"),
      getValue: () => [moment().subtract(1, "years"), moment()]
    },
    {
      label: t("dateRange.last5years"),
      getValue: () => [moment().subtract(5, "years"), moment()]
    }
  ];

  const [startLabel, endLabel] = titles ?? [
    t("dateRange.startDate"),
    t("dateRange.endDate")
  ];

  // The flex sizing has to live on the textField slot, NOT on the DatePicker's
  // own `sx`: slotProps.textField.sx replaces the root sx rather than merging
  // with it, so anything set there is silently dropped and the fields fall back
  // to full width and stack.
  //
  // "06/07/2026" at 16px needs ~90px, plus the field padding and the calendar
  // button. 150px is the floor that keeps the year whole; below it the text
  // clips. The pair only wraps to a second line when even that will not fit,
  // which happens in the narrowest filter columns.
  const sharedSlotProps = {
    textField: {
      sx: { ...outlinedFieldSx, flex: "1 1 150px", minWidth: "150px" }
    },
    // Shrunk so the pair still fits side by side in a 320px column — the
    // default button costs about 10px more per field.
    openPickerButton: { size: "small" as const, edge: "end" as const },
    desktopPaper: { sx: pickerPaperSx },
    mobilePaper: { sx: pickerPaperSx }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: "8px"
        }}
      >
        <DatePicker
          label={startLabel}
          value={start}
          // A start after the current end would describe an empty range.
          maxDate={end ?? undefined}
          disableFuture
          onChange={newStart => onChange([newStart, end])}
          slotProps={sharedSlotProps}
        />
        <DatePicker
          label={endLabel}
          value={end}
          minDate={start ?? undefined}
          disableFuture
          onChange={newEnd => onChange([start, newEnd])}
          slotProps={sharedSlotProps}
        />
      </Box>

      {/* The Pro picker offered these inside its popup; with two separate
          pickers the only place they can live is under the fields. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {shortcuts.map(shortcut => (
          <Box
            key={shortcut.label}
            component="button"
            type="button"
            onClick={() => onChange(shortcut.getValue())}
            sx={shortcutChipSx}
          >
            {shortcut.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
